<template>
    <div class="overview-container">
        <div class="tree-wrap" role="tree">
            <div class="tree">
                <TreeNodeItem v-for="n in nodes" :key="n.id" :node="n" :depth="0" :is-root="true"
                    @node-click="onNodeClick" />
            </div>
        </div>
    </div>

    <!-- Workgroup properties modal -->
    <ContentModal v-model="showWgModal" :title="wgModalTitle" size="lg">
        <div v-if="wgMeta" class="wg-props-grid">
            <div v-for="(item, idx) in wgPropsList" :key="idx" class="wg-prop-row">
                <span class="wg-prop-label">{{ item.label }}</span>
                <span class="wg-prop-value" :class="item.cls">{{ item.value }}</span>
            </div>
        </div>
    </ContentModal>

    <!-- Instance Workgroup assigned persons modal -->
    <ContentModal v-model="showIwgModal" :title="iwgModalTitle" size="lg">
        <table v-if="iwgPersons && iwgPersons.length" class="iwg-table">
            <thead>
                <tr><th>Name</th><th>Email</th><th>Status</th><th>Response Method</th></tr>
            </thead>
            <tbody>
                <tr v-for="p in iwgPersons" :key="p.personid">
                    <td>{{ p.name }}</td>
                    <td>{{ p.email }}</td>
                    <td>
                        <span class="iwg-status" :class="'iwg-status-' + p.status_label?.toLowerCase()">{{ p.status_label }}</span>
                    </td>
                    <td>{{ p.response_method || '—' }}</td>
                </tr>
            </tbody>
        </table>
        <p v-else class="text-muted text-center py-3">No users assigned to this instance workgroup.</p>
    </ContentModal>
</template>

<script>
import { defineComponent, ref, watch, computed } from 'vue'
import TreeNodeItem from './TreeNodeItem.vue'
import ContentModal from '@/components/Modal/ContentModal.vue'

export default defineComponent({
    components: { TreeNodeItem, ContentModal },
    props: {
        tree: {
            type: [Array, Object],
            default: null,
        },
    },
    setup(props) {
        const nodes = ref([]);

        const updateNodes = (tree) => {
            if (Array.isArray(tree)) {
                nodes.value = tree;
            } else if (tree && tree.root) {
                nodes.value = [tree.root];
            } else if (tree) {
                nodes.value = [tree];
            } else {
                nodes.value = [];
            }
        };

        watch(() => props.tree, (newTree) => {
            updateNodes(newTree);
        }, { immediate: true });

        // ── Workgroup properties modal ──
        const showWgModal = ref(false);
        const wgMeta = ref(null);
        const wgModalTitle = ref('');

        const yn = (v) => v ? 'Yes' : 'No';
        const yesNo = (v) => ({ value: yn(v), cls: v ? 'text-success' : 'text-danger' });

        const wgPropsList = computed(() => {
            if (!wgMeta.value) return [];
            const m = wgMeta.value;
            return [
                { label: 'Persons Needed', ...yesNo(true), value: m.persons_needed },
                { label: 'Skill', value: m.skill_name || '—', cls: '' },
                { label: 'Priority', value: m.priority, cls: '' },
                { label: 'Auto-Schedule', ...yesNo(m.auto_schedule) },
                { label: 'Auto-Substitute', ...yesNo(m.auto_substitute) },
                { label: 'Auto-Decline', ...yesNo(m.auto_decline) },
                { label: 'Sign-Up Only', ...yesNo(m.sign_up_only) },
                { label: 'Convert to Sign-Up', ...yesNo(m.convert_to_signup) },
                { label: 'Enable Swap', ...yesNo(m.enable_swap) },
                { label: 'Allow Overlap', ...yesNo(m.allow_overlap) },
                { label: 'Publicly Viewable', ...yesNo(m.publicly_viewable) },
                { label: 'Teams Enabled', ...yesNo(m.teams_enabled) },
                { label: 'Send SMS', ...yesNo(m.send_sms) },
                { label: 'Decline Days', value: `${m.decline_days} day(s)`, cls: '' },
            ];
        });

        // ── Instance Workgroup persons modal ──
        const showIwgModal = ref(false);
        const iwgPersons = ref([]);
        const iwgModalTitle = ref('');

        const onNodeClick = ({ type, node }) => {
            if (type === 'workgroup' && node.meta) {
                wgMeta.value = node.meta;
                wgModalTitle.value = node.label;
                showWgModal.value = true;
            } else if (type === 'instance-workgroup' && node.meta) {
                iwgPersons.value = node.meta.assigned_persons || [];
                iwgModalTitle.value = node.label;
                showIwgModal.value = true;
            }
        };

        return { nodes, showWgModal, wgMeta, wgModalTitle, wgPropsList, showIwgModal, iwgPersons, iwgModalTitle, onNodeClick };
    },
})
</script>

<style scoped>
.overview-container { height: 100%; display: flex; flex-direction: column; min-height: 0; }
.tree-wrap { padding: 24px; background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); color: #1e293b; overflow-x: auto; overflow-y: auto; border: 1px solid #e2e8f0; border-radius: 12px; flex: 1 1 auto; min-height: 0; height: 100%; }
.tree { display: inline-block; min-width: 480px; }

/* Workgroup properties grid */
.wg-props-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 16px; }
.wg-prop-row { display: flex; justify-content: space-between; padding: 8px 12px; background: #f8fafc; border-radius: 6px; font-size: 0.85rem; }
.wg-prop-label { color: #64748b; font-weight: 500; }
.wg-prop-value { font-weight: 600; color: #344767; }
.text-success { color: #16a34a !important; }
.text-danger { color: #dc2626 !important; }

/* Instance workgroup persons table */
.iwg-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
.iwg-table th { background: #f1f5f9; padding: 8px 12px; text-align: left; font-weight: 600; color: #475569; border-bottom: 2px solid #e2e8f0; }
.iwg-table td { padding: 8px 12px; border-bottom: 1px solid #f1f5f9; }
.iwg-table tr:hover td { background: #f8fafc; }
.iwg-status { padding: 2px 10px; border-radius: 12px; font-size: 0.75rem; font-weight: 600; }
.iwg-status-confirmed { background: #dcfce7; color: #166534; }
.iwg-status-pending { background: #fef9c3; color: #854d0e; }
.iwg-status-declined { background: #fef2f2; color: #991b1b; }
</style>
